import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Save, BarChart3, ChevronRight, Leaf, Users, Shield, Layers, BookOpen, PanelRightClose, PanelRightOpen, Send, Lock, Unlock } from "lucide-react";
import { toast } from "sonner";
import { EvidenceUploader } from "@/components/EvidenceUploader";
import { EvidencePanel } from "@/components/EvidencePanel";
import { hasRole } from "@/lib/roles";

const PILLAR_META = {
  E: { label: "Environmental", color: "viva", icon: Leaf, bg: "bg-viva-50", text: "text-viva-700", ring: "ring-viva-500" },
  S: { label: "Social", color: "amber", icon: Users, bg: "bg-amber-50", text: "text-amber-700", ring: "ring-amber-500" },
  G: { label: "Governance", color: "indigo", icon: Shield, bg: "bg-indigo-50", text: "text-indigo-700", ring: "ring-indigo-500" },
};

export default function DataEntry() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isCliente = hasRole(user, "cliente");
  const [assessment, setAssessment] = useState(null);
  const [evidencePanelOpen, setEvidencePanelOpen] = useState(
    () => typeof window !== "undefined" ? window.innerWidth >= 1280 : true,
  );
  const [indicators, setIndicators] = useState([]);
  const [topicWeights, setTopicWeights] = useState([]);
  const [clusterBench, setClusterBench] = useState({}); // {code: {p10_value, p90_value}}
  const [answers, setAnswers] = useState({}); // {code: {answer_text, numeric_value, ...}}
  const [scores, setScores] = useState({}); // local computed scores
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activePillar, setActivePillar] = useState("E");

  useEffect(() => {
    (async () => {
      try {
        const [a, ans, tw] = await Promise.all([
          api.get(`/assessments/${id}`),
          api.get(`/assessments/${id}/answers`),
          api.get("/topic-weights"),
        ]);
        setAssessment(a.data);
        const [inds, bench] = await Promise.all([
          api.get(`/indicators?profile=${a.data.profile_level}`),
          api.get(`/benchmarks?cluster=${a.data.cluster_code}`),
        ]);
        setIndicators(inds.data);
        setTopicWeights(tw.data);
        const bm = {};
        for (const b of bench.data) bm[b.indicator_code] = b;
        setClusterBench(bm);
        const map = {};
        for (const x of ans.data) {
          map[x.indicator_code] = {
            answer_text: x.answer_text || "",
            numeric_value: x.numeric_value,
            client_note: x.client_note || "",
            auditor_note: x.auditor_note || "",
          };
        }
        setAnswers(map);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  // Local score computation (mirror backend, uses cluster benchmark if present)
  const computeLocal = useCallback((ind, ans) => {
    const a = ans || {};
    const text = (a.answer_text || "").toUpperCase();
    if (ind.type === "B") {
      if (text === "" || text === "NA") return null;
      if (ind.direction === "pos") return text === "SI" ? 100 : 0;
      return text === "SI" ? 0 : 100;
    }
    if (ind.type === "L") {
      const m = { L1: 0, L2: 25, L3: 50, L4: 75, L5: 100 };
      if (text === "NA" || text === "") return null;
      return m[text] ?? null;
    }
    if (ind.type === "Q") {
      const v = a.numeric_value;
      if (v === null || v === undefined || text === "NA") return null;
      const b = clusterBench[ind.code];
      const p10 = b ? b.p10_value : (ind.p10 ?? 0);
      const p90 = b ? b.p90_value : (ind.p90 ?? 100);
      if (p90 === p10) return 0;
      let raw = ind.direction === "neg"
        ? 100 * (1 - (v - p10) / (p90 - p10))
        : 100 * ((v - p10) / (p90 - p10));
      return Math.max(0, Math.min(100, raw));
    }
    return null;
  }, [clusterBench]);

  useEffect(() => {
    const s = {};
    for (const ind of indicators) s[ind.code] = computeLocal(ind, answers[ind.code]);
    setScores(s);
  }, [indicators, answers, computeLocal]);

  // Group by pillar > topic
  const grouped = useMemo(() => {
    const g = { E: {}, S: {}, G: {} };
    for (const ind of indicators) {
      if (!g[ind.pillar][ind.topic_code]) g[ind.pillar][ind.topic_code] = [];
      g[ind.pillar][ind.topic_code].push(ind);
    }
    return g;
  }, [indicators]);

  const topicLabel = (code) => topicWeights.find((t) => t.topic_code === code)?.label || code;

  // Pillar-level live score (simple unweighted average of topic averages with renormalization on answered topics)
  const pillarLive = (pillar) => {
    const topics = grouped[pillar] || {};
    const topicCodes = Object.keys(topics).sort();
    let num = 0, den = 0;
    for (const tc of topicCodes) {
      const inds = topics[tc];
      const sList = inds.map((i) => scores[i.code]).filter((v) => v !== null && v !== undefined);
      if (sList.length === 0) continue;
      const tscore = sList.reduce((a, b) => a + b, 0) / sList.length;
      const w = topicWeights.find((x) => x.topic_code === tc)?.weight || 0;
      num += tscore * w;
      den += w;
    }
    return den > 0 ? num / den : null;
  };

  async function save() {
    setSaving(true);
    try {
      const payload = Object.entries(answers).map(([code, a]) => ({
        indicator_code: code,
        answer_text: a.answer_text || "",
        numeric_value: a.numeric_value ?? null,
        client_note: a.client_note || "",
        auditor_note: a.auditor_note || "",
      }));
      await api.put(`/assessments/${id}/answers`, { answers: payload });
      toast.success("Risposte salvate");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore di salvataggio");
    } finally { setSaving(false); }
  }

  // Lock state derives from assessment status + admin_unlock workflow.
  //   - cliente:  locked when status != "draft"
  //   - admin/valuatore: locked when status != "draft" AND no active admin_unlock
  //   - other roles (revisore/etc.): always read-only (handled elsewhere)
  const submitted = Boolean(assessment?.status && assessment.status !== "draft");
  const adminUnlock = assessment?.admin_unlock || null;
  const isLocked = submitted && (isCliente || !adminUnlock);

  const [submitting, setSubmitting] = useState(false);
  async function submitToViva() {
    const confirmMsg = (
      "Confermi l'INVIO definitivo del questionario a VIVA ESG?\n\n" +
      "• Le risposte non verranno più modificate da te\n" +
      "• Il team VIVA riceverà notifica per avviare il riesame\n" +
      "• Per modifiche successive dovrai contattare VIVA ESG"
    );
    if (!window.confirm(confirmMsg)) return;
    setSubmitting(true);
    try {
      // Save current state first
      const payload = Object.entries(answers).map(([code, a]) => ({
        indicator_code: code,
        answer_text: a.answer_text || "",
        numeric_value: a.numeric_value ?? null,
        client_note: a.client_note || "",
        auditor_note: a.auditor_note || "",
      }));
      await api.put(`/assessments/${id}/answers`, { answers: payload });
      // Then transition workflow
      await api.post(`/assessments/${id}/workflow/transition`, {
        target_status: "domanda_inviata",
        note: "Inviato dal cliente dalla pagina di compilazione",
      });
      toast.success("Questionario inviato a VIVA ESG. Riceverai una conferma via email.");
      // Reload assessment so UI locks
      const a = await api.get(`/assessments/${id}`);
      setAssessment(a.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Invio fallito");
    } finally { setSubmitting(false); }
  }

  // Admin unlock / lock — official procedure with audit trail
  const [unlocking, setUnlocking] = useState(false);
  async function adminUnlockNow() {
    const reason = window.prompt(
      "SBLOCCO UFFICIALE\n\n" +
      "Inserisci la motivazione (verrà registrata nell'audit log):\n" +
      "Es. «Errore di compilazione segnalato dal cliente, correzione concordata via email»",
      "",
    );
    if (reason === null) return;
    if (!reason.trim() || reason.trim().length < 5) {
      toast.error("Motivazione obbligatoria (almeno 5 caratteri)");
      return;
    }
    setUnlocking(true);
    try {
      await api.post(`/assessments/${id}/admin-unlock`, { reason: reason.trim() });
      toast.success("Sblocco ufficiale registrato. Puoi ora modificare le risposte.");
      const a = await api.get(`/assessments/${id}`);
      setAssessment(a.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Sblocco fallito");
    } finally { setUnlocking(false); }
  }
  async function adminLockNow() {
    if (!window.confirm("Bloccare di nuovo il questionario? Le modifiche già salvate restano.")) return;
    setUnlocking(true);
    try {
      await api.post(`/assessments/${id}/admin-lock`);
      toast.success("Questionario ribloccato");
      const a = await api.get(`/assessments/${id}`);
      setAssessment(a.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore");
    } finally { setUnlocking(false); }
  }

  function updateAnswer(code, patch) {
    setAnswers((prev) => ({ ...prev, [code]: { ...(prev[code] || {}), ...patch } }));
  }

  if (loading) return <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-viva-500" /></div>;
  if (!assessment) return <div className="p-8">Assessment non trovato.</div>;

  const M = PILLAR_META[activePillar];

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Sticky pillar nav */}
      <div className="w-72 shrink-0 border-r border-slate-100 bg-white h-full overflow-y-auto no-scrollbar">
        <div className="p-6 border-b border-slate-100">
          <div className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">Assessment</div>
          <div className="font-heading font-semibold text-slate-900 mt-1 truncate">{assessment.organization?.ragione_sociale}</div>
          <div className="text-xs text-slate-500 mt-1">{assessment.profile_level} · {assessment.cluster_code} · {assessment.year}</div>
        </div>
        <div className="p-3 space-y-1">
          {["E", "S", "G"].map((p) => {
            const meta = PILLAR_META[p];
            const Icon = meta.icon;
            const score = pillarLive(p);
            return (
              <button
                key={p}
                data-testid={`pillar-nav-${p}`}
                onClick={() => setActivePillar(p)}
                className={`w-full text-left px-3 py-3 rounded-lg flex items-center gap-3 transition ${
                  activePillar === p ? `${meta.bg} ${meta.text}` : "hover:bg-slate-50 text-slate-700"
                }`}
              >
                <Icon className="w-5 h-5" strokeWidth={1.75} />
                <div className="flex-1">
                  <div className="font-medium text-sm">{meta.label}</div>
                  <div className="text-xs opacity-70">Pilastro {p}</div>
                </div>
                {!isCliente && (
                  <div className="font-heading font-bold text-base" data-testid={`pillar-live-score-${p}`}>
                    {score === null ? "—" : Math.round(score)}
                  </div>
                )}
              </button>
            );
          })}
        </div>
        <div className={`p-3 border-t border-slate-100 mt-3 transition-colors ${M.bg}`} data-testid="topic-list-frame">
          <div className={`text-xs font-semibold uppercase tracking-wider mb-2 px-3 ${M.text}`}>Topic — {M.label}</div>
          {Object.keys(grouped[activePillar] || {}).sort().map((tc) => {
            const inds = grouped[activePillar][tc];
            const answered = inds.filter((i) => scores[i.code] !== null && scores[i.code] !== undefined).length;
            return (
              <a
                key={tc}
                href={`#topic-${tc}`}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-700 hover:bg-white/70"
              >
                <ChevronRight className="w-3 h-3 text-slate-400" />
                <div className="flex-1 truncate">{tc} — {topicLabel(tc)}</div>
                <div className="text-xs text-slate-500">{answered}/{inds.length}</div>
              </a>
            );
          })}
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 min-w-0 flex flex-col h-full overflow-hidden">
        {/* Fixed top bar */}
        <div className="shrink-0 z-10 glass border-b border-slate-100 px-6 py-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <h1 className="font-heading text-xl xl:text-2xl font-bold text-slate-900 truncate">Compilazione · {M.label}</h1>
            <div className="flex items-center gap-2 mt-1">
              <p className="text-xs xl:text-sm text-slate-500 hidden md:block">{isCliente ? "Inserisci le risposte e carica le evidenze documentali." : "Inserisci le risposte e visualizza lo score in tempo reale."}</p>
              <StatusPill status={assessment?.status} />
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <Button
              variant="outline"
              onClick={() => setEvidencePanelOpen(!evidencePanelOpen)}
              data-testid="toggle-evidence-panel"
              title={evidencePanelOpen ? "Nascondi pannello evidenze" : "Mostra pannello evidenze"}
            >
              {evidencePanelOpen ? <PanelRightClose className="w-4 h-4" /> : <PanelRightOpen className="w-4 h-4" />}
            </Button>
            <Button onClick={save} disabled={saving || isLocked} data-testid="save-answers-button" className="bg-viva-500 hover:bg-viva-600 text-white disabled:opacity-40" title={isLocked ? "Questionario inviato — non modificabile" : "Salva le risposte"}>
              {saving ? <Loader2 className="w-4 h-4 xl:mr-2 animate-spin" /> : <Save className="w-4 h-4 xl:mr-2" />}
              <span className="hidden xl:inline">Salva</span>
            </Button>
            {/* Submit button for cliente (Fase 1 Disciplinare § 6.5) */}
            {isCliente && !isLocked && (
              <Button
                onClick={submitToViva}
                disabled={submitting}
                data-testid="submit-to-viva-button"
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
                title="Invia il questionario completato a VIVA ESG (azione definitiva)"
              >
                {submitting ? <Loader2 className="w-4 h-4 xl:mr-2 animate-spin" /> : <Send className="w-4 h-4 xl:mr-2" />}
                <span className="hidden xl:inline">Invia a VIVA ESG</span>
              </Button>
            )}
            {!isCliente && (
              <>
                <Button variant="outline" onClick={() => navigate(`/assessment/${id}/materialita`)} data-testid="goto-materiality-button" title="Materialità">
                  <Layers className="w-4 h-4 xl:mr-2" /> <span className="hidden xl:inline">Materialità</span>
                </Button>
                <Button variant="outline" onClick={() => navigate(`/assessment/${id}/disclosure`)} data-testid="goto-disclosure-button" title="Disclosure">
                  <BookOpen className="w-4 h-4 xl:mr-2" /> <span className="hidden xl:inline">Disclosure</span>
                </Button>
                <Button variant="outline" onClick={() => navigate(`/assessment/${id}/risultati`)} data-testid="goto-results-button" title="Risultati">
                  <BarChart3 className="w-4 h-4 xl:mr-2" /> <span className="hidden xl:inline">Risultati</span>
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Status banners (post-submit) — role-aware */}
        {submitted && isCliente && (
          <div className="px-4 py-3 bg-amber-50 border-b border-amber-200 flex items-start gap-3" data-testid="cliente-locked-banner">
            <Lock className="w-5 h-5 text-amber-700 mt-0.5 shrink-0" />
            <div className="text-sm">
              <div className="font-semibold text-amber-900">Questionario inviato a VIVA ESG — modifiche bloccate</div>
              <div className="text-amber-800 mt-0.5">
                Le tue risposte sono state inviate il {assessment.updated_at?.slice(0, 10)} e sono ora in revisione presso il team VIVA.
                Per modifiche, contatta <a href="mailto:tecnico@vivaesg.it" className="underline font-medium">tecnico@vivaesg.it</a>.
              </div>
            </div>
          </div>
        )}
        {submitted && !isCliente && !adminUnlock && (
          <div className="px-4 py-3 bg-amber-50 border-b border-amber-200 flex items-start gap-3" data-testid="admin-locked-banner">
            <Lock className="w-5 h-5 text-amber-700 mt-0.5 shrink-0" />
            <div className="text-sm flex-1">
              <div className="font-semibold text-amber-900">🔒 Questionario bloccato (cliente ha inviato la domanda)</div>
              <div className="text-amber-800 mt-0.5">
                Risposte e evidenze sono in <strong>sola lettura</strong>. Per modifiche è richiesto uno <strong>sblocco ufficiale</strong> tracciato nell'audit log.
                Contattare il cliente prima di intervenire sulle sue dichiarazioni.
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={adminUnlockNow}
              disabled={unlocking}
              data-testid="admin-unlock-button"
              className="border-amber-400 text-amber-800 hover:bg-amber-100 shrink-0"
            >
              {unlocking ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Unlock className="w-4 h-4 mr-1" />}
              Sblocca per modifica
            </Button>
          </div>
        )}
        {submitted && !isCliente && adminUnlock && (
          <div className="px-4 py-3 bg-orange-50 border-b border-orange-300 flex items-start gap-3" data-testid="admin-unlocked-banner">
            <Unlock className="w-5 h-5 text-orange-700 mt-0.5 shrink-0" />
            <div className="text-sm flex-1">
              <div className="font-semibold text-orange-900">🔓 Sblocco ufficiale attivo · modifica abilitata</div>
              <div className="text-orange-800 mt-0.5">
                Sbloccato da <strong>{adminUnlock.unlocked_by_name || adminUnlock.unlocked_by}</strong> il {adminUnlock.unlocked_at?.slice(0, 10)} · <em>«{adminUnlock.reason}»</em>
                <br />
                <span className="text-orange-700">Ricorda di <strong>ribloccare</strong> il questionario al termine delle modifiche.</span>
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={adminLockNow}
              disabled={unlocking}
              data-testid="admin-lock-button"
              className="border-orange-400 text-orange-800 hover:bg-orange-100 shrink-0"
            >
              {unlocking ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Lock className="w-4 h-4 mr-1" />}
              Blocca di nuovo
            </Button>
          </div>
        )}

        {/* Scrollable questions area — soft viva green */}
        <div className="flex-1 overflow-y-auto bg-viva-50/40">
          <div className="py-4 px-2 space-y-4">
            {Object.keys(grouped[activePillar] || {}).sort().map((tc) => (
              <Card key={tc} id={`topic-${tc}`} className="rounded-2xl border-slate-100 shadow-soft overflow-hidden" data-testid={`topic-card-${tc}`}>
                <div className={`px-5 py-2 ${M.bg} border-b ${M.text} flex items-baseline gap-3`}>
                  <span className="text-[10px] font-bold uppercase tracking-[0.18em] opacity-75 shrink-0">Topic {tc}</span>
                  <span className="font-heading text-base font-semibold truncate">{topicLabel(tc)}</span>
                </div>
                <div className="divide-y divide-slate-100">
                  {grouped[activePillar][tc].map((ind) => (
                    <IndicatorRow
                      key={ind.code}
                      indicator={ind}
                      answer={answers[ind.code] || {}}
                      score={scores[ind.code]}
                      bench={clusterBench[ind.code]}
                      onChange={(patch) => updateAnswer(ind.code, patch)}
                      assessmentId={id}
                      locked={isLocked}
                      showScore={!isCliente}
                    />
                  ))}
                </div>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Right-side evidence panel — collapsible */}
      {evidencePanelOpen && (
        <EvidencePanel assessmentId={id} />
      )}
    </div>
  );
}

// Indicators that explicitly allow N/A as a valid answer.
// All other indicators must NOT show the N/A option (frontend hides it; back-end
// values previously saved as NA are still accepted and rendered for editability,
// to avoid breaking historical data).
const NA_ALLOWED_CODES = new Set([
  "E1.08", "E2.04", "E3.06", "E5.03", "E5.05", "E5.06",
  "S7.01", "S4.05",
  "G2.01", "G1.07", "G2.03", "G2.04", "G2.05",
]);

function IndicatorRow({ indicator, answer, score, bench, onChange, assessmentId, locked, showScore = true }) {
  const text = answer.answer_text || "";
  const hasNote = Boolean((answer.client_note || "").trim());
  // Show N/A option only for whitelisted indicators OR if the user already
  // saved "NA" previously (so legacy answers remain editable).
  const allowsNA = NA_ALLOWED_CODES.has(indicator.code) || text === "NA";
  // When locked, the onChange is a no-op (defensive — also UI is disabled)
  const guardedChange = locked ? () => {} : onChange;
  return (
    <div className="px-6 py-5 grid grid-cols-1 md:grid-cols-12 gap-4 items-start" data-testid={`indicator-row-${indicator.code}`}>
      <div className="md:col-span-1 text-xs font-semibold text-slate-400 mt-2 flex items-center gap-1.5">
        <span>{indicator.code}</span>
        {hasNote && (
          <span
            data-testid={`note-indicator-${indicator.code}`}
            title="Il cliente ha scritto una nota"
            aria-label="Nota cliente presente"
            className="inline-block w-2 h-2 rounded-full bg-viva-500 ring-2 ring-viva-100"
          />
        )}
      </div>
      <div className={showScore ? "md:col-span-8" : "md:col-span-9"}>
        <div className="text-sm font-medium text-slate-900 leading-snug whitespace-pre-line">{indicator.question_it}</div>
        <div className="mt-1 flex items-center gap-2 text-[11px] uppercase tracking-wider flex-wrap">
          <span className="text-slate-400">Tipo: {indicator.type === "B" ? "SI/NO" : indicator.type === "L" ? "Likert" : "Quantitativo"}</span>
          {indicator.unit && <span className="text-slate-400">· {indicator.unit}</span>}
          {indicator.type === "Q" && bench && (
            <span className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full normal-case tracking-normal">
              {indicator.direction === "neg" ? "↓ migliore" : "↑ migliore"} · P10 {bench.p10_value} · P90 {bench.p90_value}
            </span>
          )}
        </div>
        {indicator.evidenza_richiesta && (
          <details className="mt-2 group" data-testid={`evidenza-${indicator.code}`}>
            <summary className="cursor-pointer text-[11px] font-semibold text-viva-700 hover:text-viva-800 list-none inline-flex items-center gap-1">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" className="opacity-70 group-open:rotate-90 transition-transform"><path d="M9 5l7 7-7 7" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>
              Evidenza richiesta
            </summary>
            <div className="mt-1.5 text-[12px] text-slate-600 leading-relaxed whitespace-pre-line bg-viva-50/60 border-l-2 border-viva-400 pl-3 py-1.5 rounded-r">
              {indicator.evidenza_richiesta}
            </div>
          </details>
        )}
        {assessmentId && (
          <EvidenceUploader assessmentId={assessmentId} indicatorCode={indicator.code} locked={locked} />
        )}
      </div>
      <div className="md:col-span-2">
        {indicator.type === "B" && (
          <Select value={text} onValueChange={(v) => guardedChange({ answer_text: v })} disabled={locked}>
            <SelectTrigger data-testid={`answer-${indicator.code}`} className="h-10 rounded-lg">
              <SelectValue placeholder="Seleziona..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="SI">SI</SelectItem>
              <SelectItem value="NO">NO</SelectItem>
              {allowsNA && <SelectItem value="NA">N/A</SelectItem>}
            </SelectContent>
          </Select>
        )}
        {indicator.type === "L" && (
          <Select value={text} onValueChange={(v) => guardedChange({ answer_text: v })} disabled={locked}>
            <SelectTrigger data-testid={`answer-${indicator.code}`} className="h-10 rounded-lg">
              <SelectValue placeholder="Livello..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="L1">L1 — Iniziale</SelectItem>
              <SelectItem value="L2">L2 — Base</SelectItem>
              <SelectItem value="L3">L3 — Intermedio</SelectItem>
              <SelectItem value="L4">L4 — Avanzato</SelectItem>
              <SelectItem value="L5">L5 — Ottimale</SelectItem>
              {allowsNA && <SelectItem value="NA">N/A</SelectItem>}
            </SelectContent>
          </Select>
        )}
        {indicator.type === "Q" && (
          <div className="flex gap-2">
            <Input
              type="number"
              step="any"
              value={answer.numeric_value ?? ""}
              onChange={(e) => guardedChange({ numeric_value: e.target.value === "" ? null : Number(e.target.value), answer_text: "" })}
              placeholder={`Valore ${indicator.unit || ""}`}
              data-testid={`answer-${indicator.code}`}
              className="h-10 rounded-lg"
              disabled={locked}
            />
            {allowsNA && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => guardedChange({ answer_text: text === "NA" ? "" : "NA", numeric_value: null })}
                className={text === "NA" ? "bg-slate-100" : ""}
                disabled={locked}
              >
                N/A
              </Button>
            )}
          </div>
        )}
      </div>
      {showScore && (
        <div className="md:col-span-1 flex justify-end">
          <ScoreBadge score={score} />
        </div>
      )}
      {/* Note del cliente — riga propria a larghezza piena, sotto domanda/risposta */}
      <div className="md:col-start-2 md:col-span-11">
        <textarea
          value={answer.client_note || ""}
          onChange={(e) => guardedChange({ client_note: e.target.value })}
          placeholder={locked ? "—" : "Note del cliente (specifiche, contesto, eccezioni…)"}
          rows={2}
          data-testid={`note-${indicator.code}`}
          disabled={locked}
          className="w-full text-xs px-3 py-2 rounded-lg border border-slate-200 bg-white/80 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-viva-500/30 focus:border-viva-500 resize-y disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed"
        />
      </div>
    </div>
  );
}

function ScoreBadge({ score }) {
  if (score === null || score === undefined) {
    return <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-500 text-xs font-semibold">—</span>;
  }
  const cls = score >= 75 ? "bg-viva-100 text-viva-700" : score >= 50 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700";
  return <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${cls}`}>{Math.round(score)}</span>;
}

// Status pill — shows current workflow phase visible to ALL roles (Disciplinare § 6.5)
// Phase numbers ALIGN with the Disciplinare: 1..10 (no draft number, sub-phases 6a/6b)
const STATUS_LABELS = {
  draft: { label: "In compilazione", color: "bg-slate-100 text-slate-700", phase: null },
  domanda_inviata: { label: "Domanda inviata", color: "bg-viva-100 text-viva-800", phase: "1" },
  contratto_firmato: { label: "Contratto firmato", color: "bg-sky-100 text-sky-800", phase: "2" },
  riesame_domanda: { label: "Riesame domanda", color: "bg-viva-100 text-viva-800", phase: "3" },
  piano_audit: { label: "Piano di audit", color: "bg-sky-100 text-sky-800", phase: "4" },
  analisi_documentale: { label: "Trasmissione documentale", color: "bg-sky-100 text-sky-800", phase: "5" },
  stage1_completato: { label: "Stage 1 audit", color: "bg-indigo-100 text-indigo-800", phase: "6a" },
  stage2_completato: { label: "Stage 2 audit", color: "bg-indigo-100 text-indigo-800", phase: "6b" },
  rating_elaborato: { label: "Rating elaborato", color: "bg-purple-100 text-purple-800", phase: "7" },
  riesame_tecnico: { label: "Riesame tecnico", color: "bg-purple-100 text-purple-800", phase: "8" },
  delibera: { label: "In delibera", color: "bg-purple-100 text-purple-800", phase: "9" },
  emesso: { label: "Certificato emesso", color: "bg-emerald-100 text-emerald-800", phase: "10" },
  sospeso: { label: "Sospeso", color: "bg-amber-100 text-amber-800", phase: null },
  revocato: { label: "Revocato", color: "bg-red-100 text-red-800", phase: null },
  scaduto: { label: "Scaduto", color: "bg-slate-200 text-slate-700", phase: null },
};

function StatusPill({ status }) {
  if (!status) return null;
  const meta = STATUS_LABELS[status] || { label: status, color: "bg-slate-100 text-slate-700", phase: null };
  return (
    <span
      data-testid="status-pill"
      className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${meta.color}`}
      title={meta.phase ? `Disciplinare DT-RTG-01 § 6.5 — Fase ${meta.phase}/10` : meta.label}
    >
      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
      {meta.phase && <span className="opacity-70">Fase {meta.phase}/10 ·</span>}
      {meta.label}
    </span>
  );
}
